import { useState, useCallback } from "react";
import { Brand } from "@/types/supabase";
import { createApiService, fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import { User } from "@auth0/auth0-spa-js";

// Interface para la respuesta paginada del API
interface PaginatedBrandsResponse {
  data: Brand[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Cache global para evitar múltiples llamadas
let brandsCache: Brand[] | null = null;
let isFetching = false;

export function useBrandsApi() {
  const [brands, setBrands] = useState<Brand[]>(brandsCache || []);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, isManager, isLoading, user, userDataLoading } =
    useAuth();

  // Wrap getAccessToken to add error handling
  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      setError("Authentication error. Please try logging in again.");
      throw error;
    }
  }, [getAccessToken]);

  // Wait for auth to be ready before hitting APIs that depend on it
  const waitForAuthReady = useCallback(
    async (maxMs = 5000, intervalMs = 100): Promise<boolean> => {
      const start = Date.now();
      const isReady = () =>
        !isLoading && !userDataLoading && (!isManager || Boolean(user?.email));
      while (!isReady()) {
        if (Date.now() - start > maxMs) break;
        await new Promise((r) => setTimeout(r, intervalMs));
      }
      return (
        !isLoading && !userDataLoading && (!isManager || Boolean(user?.email))
      );
    },
    [isLoading, userDataLoading, isManager, user?.email],
  );

  // Create brands API service
  const brandsService = createApiService<Brand>(
    "/branch/brands",
    getTokenWithErrorHandling,
  );

  const fetchBrandById = async (id: number) => {
    const token = await getTokenWithErrorHandling();
    const response = await fetchWithAuth(`/branch/brands/${id}`, token);
    return response;
  };

  // Fetch paginated brands
  const fetchBrandsPaginated = async ({
    page = 1,
    limit = 10,
    search,
    brandIds,
    user,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    brandIds?: number[] | null;
    user?: User | null;
  }) => {
    setLoading(true);
    try {
      const ready = await waitForAuthReady();
      if (!ready) {
        setLoading(false);
        return;
      }
      const token = await getTokenWithErrorHandling();
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const brandIdsParam =
        brandIds && brandIds.length > 0
          ? `&brandIds=${brandIds.join(",")}`
          : "";

      const userParam =
        user && isManager
          ? `&email=${encodeURIComponent(user.email || "")}`
          : "";
      const url = `/branch/brands?page=${page}&limit=${limit}${searchParam}${brandIdsParam}${userParam}`;
      const response = await fetchWithAuth(url, token);
      if (!response) throw new Error("Error al obtener las marcas");

      setBrands(response.data);
      setTotalCount(response.total);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
      setError(null);
    } catch (err) {
      console.error("Error fetching paginated brands:", err);
      setError("Error al obtener las marcas");
      toastHandler({
        description: "Error al obtener las marcas",
        variant: "destructive",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all brands without pagination (para dropdowns, filtros, etc.)
  const fetchAllBrands = useCallback(
    async (user?: User | null) => {
      // Si ya tenemos datos en cache, usarlos
      if (brandsCache && brandsCache.length > 0) {
        setBrands(brandsCache);
        return;
      }

      // Si ya hay una llamada en progreso, esperar a que termine
      if (isFetching) {
        const promise = new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (!isFetching && brandsCache) {
              setBrands(brandsCache);
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
        return promise;
      }

      isFetching = true;
      setLoading(true);
      try {
        const ready = await waitForAuthReady();
        if (!ready) {
          isFetching = false;
          setLoading(false);
          return;
        }
        const token = await getTokenWithErrorHandling();

        // Usar fetchWithAuth directamente para poder pasar parámetros de consulta
        const response = await fetchWithAuth(
          `/branch/brands?limit=100${user && isManager ? `&email=${encodeURIComponent(user.email || "")}` : ""}`,
          token,
        );
        // Verificar si la respuesta es un objeto paginado o un array directo
        let brandsData: Brand[];
        if (response && typeof response === "object" && "data" in response) {
          // Es una respuesta paginada
          brandsData = (response as PaginatedBrandsResponse).data;
        } else if (Array.isArray(response)) {
          // Es un array directo
          brandsData = response;
        } else {
          // Respuesta inesperada, usar array vacío
          brandsData = [];
        }
        // Guardar en cache global
        brandsCache = brandsData;
        setBrands(brandsData);
        setError(null);
      } catch (err) {
        console.error("Error fetching all brands:", err);
        setError("Error al obtener las marcas");
        toastHandler({
          description: "Error al obtener las marcas",
          variant: "destructive",
          title: "Error",
        });
      } finally {
        isFetching = false;
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );
  // Create a new brand
  const createBrand = async (brandData: {
    name: string;
    countryId?: number;
    groupingDistanceMeters?: number;
  }) => {
    try {
      const { name, countryId, groupingDistanceMeters } = brandData;
      const payload = {
        name,
        countryId,
        grouping_distance_meters: groupingDistanceMeters,
      };
      const token = await getAccessToken();
      const newBrand = await fetchWithAuth(`/branch/brands`, token, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      // Actualizar cache
      if (brandsCache) {
        brandsCache = [...brandsCache, newBrand].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      }
      setBrands((prev) =>
        [...prev, newBrand].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setTotalCount((prev) => prev + 1);
      toastHandler({
        description: `Marca "${newBrand.name}" creada correctamente`,
        title: "Success",
        variant: "success",
      });
      return newBrand;
    } catch (error) {
      console.error("Error creating brand:", error);
      toastHandler({
        description:
          error instanceof Error ? error.message : "Failed to create brand",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  // Update a brand
  const updateBrand = async (id: number, brandData: Partial<Brand>) => {
    try {
      const token = await getAccessToken();
      const updatedBrand = await fetchWithAuth(`/branch/brands/${id}`, token, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(brandData),
      });
      // Actualizar cache
      if (brandsCache) {
        brandsCache = brandsCache.map((brand) =>
          brand.id === id ? { ...brand, ...updatedBrand } : brand,
        );
      }
      setBrands((prev) =>
        prev.map((brand) =>
          brand.id === id ? { ...brand, ...updatedBrand } : brand,
        ),
      );

      return updatedBrand;
    } catch (error) {
      console.error("Error updating brand:", error);
      toastHandler({
        description:
          error instanceof Error ? error.message : "Failed to update brand",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  // Delete a brand
  const deleteBrand = async (id: number) => {
    try {
      await brandsService.delete(id);
      // Actualizar cache
      if (brandsCache) {
        brandsCache = brandsCache.filter((brand) => brand.id !== id);
      }
      setBrands((prev) => prev.filter((brand) => brand.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      toastHandler({
        description: "Brand deleted successfully",
        title: "Success",
        variant: "success",
      });
    } catch (error) {
      console.error("Error deleting brand:", error);
      toastHandler({
        description:
          error instanceof Error ? error.message : "Failed to delete brand",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  return {
    brands,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
    fetchAllBrands,
    fetchBrandsPaginated,
    createBrand,
    updateBrand,
    deleteBrand,
    fetchBrandById,
  };
}
